import { motion, useTransform, type MotionValue } from 'framer-motion';

import PlayerCard, { type PlayerCardProps } from '@/components/cards/PlayerCard';

/** PlayerCard's intrinsic width used to centre the unscaled box */
const CARD_WIDTH = 110;

export const SELECTED_BORDER = '#3CBE1E';

type CarouselCardProps = {
  card: PlayerCardProps;
  // Slot relative to active card: 0 is front, -1 left, +1 right
  slot: number;
  // Live drag offset shard by every card
  dragX: MotionValue<number>;
  // Distance between slots
  step: number;
  // Scale of front card
  frontScale: number;
  // Scale of card one slot out
  sideScale: number;
  // Distance from the design box's bottom edge
  bottom: number;
  onSelect?: () => void;
  isSelected?: boolean;
  showBadge?: boolean;
};

/**
 * One card in the reveal carousel. It's position is `slot + drag progress` and every visual property is interpolated from that, so the cards can rotate through the front slot instead of the whole row sliding sideways.
 */
export default function CarouselCard({
  card,
  slot,
  dragX,
  step,
  frontScale,
  sideScale,
  bottom,
  onSelect,
  isSelected,
  showBadge,
}: CarouselCardProps) {
  const position = useTransform(dragX, v => slot + v / step);

  const x = useTransform(position, p => p * step);
  const scale = useTransform(
    position,
    p => frontScale * (1 - Math.min(Math.abs(p), 1) * (1 - sideScale))
  );
  // Depth cue: sharp and bright at front, blurred and dimmed once
  // out to the side.
  const filter = useTransform(position, p => {
    const t = Math.min(Math.abs(p), 1);
    return `blur(${(0.6 * t).toFixed(2)}px) brightness(${(1 - 0.25 * t).toFixed(3)}) grayscale(${Math.round(20 * t)}%)`;
  });
  // Cards beyond the two side slots are duplicates of the visible ones when the
  // candidate count is small, so they're hidden rather than shown twice.
  // Fade the far copies out rather than stepping, so the duplicate that exists
  // when there are fewer candidates than slots can't pop in and out mid-drag.
  const opacity = useTransform(position, p => {
    const a = Math.abs(p);
    if (a <= 1.2) return 1;
    if (a >= 1.5) return 0;
    return 1 - (a - 1.2) / 0.3;
  });
  const zIndex = useTransform(position, p => Math.round(10 - Math.abs(p) * 5));

  const isFront = slot === 0;

  return (
    <motion.div
      className="absolute"
      style={{
        bottom,
        left: '50%',
        marginLeft: -CARD_WIDTH / 2,
        x,
        scale,
        filter,
        opacity,
        zIndex,
        transformOrigin: 'bottom center',
        // Only the front card is interactive; the rest are depth.
        pointerEvents: isFront ? 'auto' : 'none',
      }}
      aria-hidden={!isFront}
      onClick={isFront ? onSelect : undefined}
    >
      <div className="relative">
        <PlayerCard {...card} borderColor={isSelected ? SELECTED_BORDER : undefined} />
        {showBadge && (
          <div
            aria-hidden
            className="absolute flex items-center justify-center rounded-full"
            style={{
              // Overlaps the card's top-right corner, as in the comp.
              top: -9,
              right: -9,
              width: 22,
              height: 22,
              background: SELECTED_BORDER,
              boxShadow: '0 1px 3px #00000059',
            }}
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path
                d="M2.5 6.2 4.8 8.5 9.5 3.8"
                stroke="#FFFFFF"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        )}
      </div>
    </motion.div>
  );
}
